import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Document, Page, pdfjs } from 'react-pdf';
import { makeStyles, Typography } from '@material-ui/core';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import DropdownMenu from '../dropdownMenu';
import Candidate from '../candidatesList/candidate';
import ActionButton from '../actionButton';
import HorizontalNavigation from '../horizontalNavigation';
import ConditionalTooltip from '../conditionalTooltip';
import Scrollbars from '../scrollbars';
import VirtualizedList from '../virtualizedList';
import CustomButton from '../customButton';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import ListWithTooltip from '../listWithTooltip';
import VoteInfo from './vote';
import CommunicationInfo from './communicationInfo';
import JobEvent from './jobEvent';
import NotificationCard from '../notificationCard';
import { ReactComponent as VoteAgainstIcon } from '../../../assets/icons/thumb_down_alt.svg';
import { ReactComponent as VoteForIcon } from '../../../assets/icons/thumb_up_alt.svg';
import { ReactComponent as PhoneIcon } from '../../../assets/icons/phone_iphone.svg';
import { ReactComponent as LocationIcon } from '../../../assets/icons/pin_drop.svg';
import { ReactComponent as PersonIcon } from '../../../assets/icons/person.svg';
import { ReactComponent as ZoomIcon } from '../../../assets/icons/_button_new.svg';
import { ReactComponent as CommentIcon } from '../../../assets/icons/comment.svg';
import { ReactComponent as EmailIcon } from '../../../assets/icons/mail.svg';
import { ReactComponent as NoRecordsIcon } from '../../../assets/icons/comments_disabled.svg';
import { VOTE_MAP } from '../voteField/config';
import {
  CANDIDATE_NAVIGATIONS,
  getAddress,
  getMenuItems,
  getMoveCandidateToStepActionItems,
  TIMELINE_MAP,
} from './config';
import { getPeriod } from '../../pages/candidateDetailsPage/config';
import {
  ADDRESS_INFO,
  CANDIDATE_PROFILE_INFO,
  CONTACT_DETAILS_INFO,
} from '../../pages/jobsPage/config';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { isEmpty } from '../../../utility/validation';
import {
  isArray,
  isArrayEmpty,
  isObjectEmpty,
  isItemInList,
} from '../../../utility/helpers';
import { API_DOMAIN, API_PROTOCOL } from '../../../utility/http';
import { getSubdomain } from '../../../utility/uiUtils';
import { ellipsis } from '../../../constants/helperCssRules';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      padding: spacing(8, 3, 6, 4),
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      [breakpoints.up('xl')]: {
        padding: spacing(8, 3, 6, 8),
      },
    },
    buttonWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    timelineWrapper: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 260px) minmax(0, 1fr)',
      gridColumnGap: 16,
      marginBottom: spacing(4),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    addressWrapper: {
      marginBottom: spacing(4),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    button: {
      padding: spacing(2, 3),
      '& span': {
        fontFamily: 'ProximaNova-Bold',
        fontSize: 14,
        letterSpacing: 'normal',
        textTransform: 'none',
      },
      [breakpoints.up('xLg')]: {
        '& span': {
          fontFamily: 'ProximaNova-Bold',
          fontSize: 16,
          letterSpacing: 'normal',
          textTransform: 'none',
        },
      },
    },
    disabledButton: {
      opacity: '20%',
      cursor: 'default',
      pointerEvents: 'none',
    },
    addCommentButton: {
      color: primary.bluish1,
      marginRight: spacing(1),
      width: 'fit-content',
      [breakpoints.up('xLg')]: {
        marginRight: spacing(2),
      },
      '&:disabled': {
        border: `1px solid ${primary.bluish7}`,
        backgroundColor: primary.white,
        '& span': {
          color: primary.bluish7,
          '& img': {
            opacity: 0.2,
          },
        },
      },
    },
    candidate: {
      marginTop: spacing(4),
    },
    infoWrapper: {
      display: 'flex',
      alignItems: 'center',
    },
    iconButton: {
      height: 40,
      width: 56,
      padding: spacing(2, 4, 2, 4),
    },
    disqualifyButton: {
      backgroundColor: primary.red1,
      borderColor: primary.red1,
      marginRight: spacing(1),
      '&:hover': {
        backgroundColor: primary.red1,
      },
      [breakpoints.up('xLg')]: {
        marginRight: spacing(2),
      },
    },
    qualifyButton: {
      backgroundColor: primary.green1,
      borderColor: primary.green1,
      marginRight: spacing(2),
      '&:hover': {
        backgroundColor: primary.green1,
      },
    },
    navWrapper: {
      marginTop: spacing(6),
      paddingBottom: 0,
    },
    noResultsNavWrapper: {
      paddingBottom: 0,
    },
    reviewsSectionWrap: {
      borderBottom: `1px solid ${primary.bluish7}`,
      paddingBottom: spacing(4),
    },
    noPendingUsers: {
      borderBottom: 'none',
      paddingBottom: 0,
    },
    sectionField: {
      marginBottom: spacing(6.75),
      color: primary.bluish2,
      lineHeight: '18px',
      textTransform: 'uppercase',
    },
    sectionData: {
      ...ellipsis(),
      maxWidth: 330,
    },
    profileWrapper: {
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      paddingTop: spacing(8),
      height: '100%',
      minHeight: 0,
    },
    profileContent: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      minHeight: 0,
    },
    profileInfoSection: {
      borderBottom: `1px solid ${primary.bluish7}`,
      paddingBottom: spacing(8),
      marginBottom: spacing(8),
      '&:last-of-type': {
        marginBottom: spacing(0),
      },
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      marginTop: spacing(8),
      minHeight: 0,
      width: '100%',
    },
    document: {
      display: 'flex',
    },
    pdfPage: {
      justifyContent: 'center',
      display: 'flex',
      width: 'fit-content',
      '& > canvas': {
        border: `1px solid ${primary.bluish7}`,
        width: '341px !important',
        height: '482px !important',
      },
    },
    scrollY: {
      right: -12,
    },
    scrollRootInfo: {
      height: 'calc(100vh - 473px) !important',
    },
    tooltip: {
      display: 'flex',
      flexShrink: 0,
    },
    scale: {
      zIndex: 99,
      position: 'absolute',
      paddingTop: spacing(1),
      left: 284,
    },
    fieldWrapper: {
      maxWidth: 'inherit',
    },
    field: {
      color: primary.bluish2,
    },
    dateField: {
      color: primary.bluish2,
      lineHeight: '18px',
    },
    voteFor: {
      paddingRight: spacing(2),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: 24,
      height: 24,
    },
    voteAgainst: {
      paddingLeft: spacing(2),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      width: 24,
      height: 24,
    },
    evaluationsLabel: {
      marginTop: spacing(3),
    },
    info: {
      marginTop: spacing(6),
      display: 'grid',
      gridTemplateColumns: `repeat(4, minmax(0, min-content))`,
      gridColumnGap: 16,
    },
    infoItem: {
      display: 'flex',
      alignItems: 'center',
      maxWidth: '100%',
    },
    icon: {
      flexShrink: 0,
      marginRight: spacing(1),
      width: 16,
      height: 16,
    },
    pendingUsers: {
      marginLeft: spacing(9),
      paddingTop: spacing(3.5),
      display: 'flex',
      alignItems: 'center',
    },
    listWithTooltip: {
      width: 'fit-content',
      marginRight: spacing(2),
    },
    notificationCard: {
      marginTop: spacing(8),
      height: 'inherit',
      justifyContent: 'center',
    },
    notificationTitleClass: {
      marginBottom: spacing(2),
    },
    notSelected: {
      opacity: 0.1,
    },
    customNavItem: {
      marginLeft: 0,
      marginRight: spacing(12),
    },
    uploadNotification: {
      backgroundColor: primary.green5,
      padding: spacing(2, 0, 2, 0),
      marginBottom: spacing(6),
    },
  })
);

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const VISIBLE_USER_COUNT = 4;

const CandidateInfo = ({
  translations,
  user,
  isTimelineLoading,
  hasLoadMoreTimeline,
  isLoadingData,
  auth,
  canEdit,
  authVote = {},
  reviews = {},
  comments = [],
  emails = [],
  timeline = {},
  steps = [],
  hiringTeam = [],
  candidateNavigationItems,
  navSection,
  isArchivedJob,
  isOngoingCvParsing,
  candidateStep,
  handleMoveToStep = () => {},
  handleDisqualify = () => {},
  handleQualify = () => {},
  handleVote = () => {},
  handleAddComment = () => {},
  handleEmailClick = () => {},
  handleEditDrawer = () => {},
  handleEditComment,
  handleDeleteComment,
  handleDeleteVote,
  onLoadMore,
}) => {
  const classes = useStyles();

  const [numberOfPages, setNumberOfPages] = useState(1);

  const hasAuthVote = !isObjectEmpty(authVote);
  const hasContactInfo =
    user[ADDRESS_INFO.ADDRESS] ||
    user[ADDRESS_INFO.CITY] ||
    user[ADDRESS_INFO.COUNTRY] ||
    user.phone ||
    user.email ||
    user.referral;

  const hasEmails = !isArrayEmpty(emails);
  const hasReviews = !isObjectEmpty(reviews);
  const hasComments = !isArrayEmpty(comments);
  const timelineResults = timeline.results;
  const hasTimelineResults =
    isArray(timelineResults) && !isArrayEmpty(timelineResults);
  const hasNoResults =
    (!hasEmails && navSection === CANDIDATE_NAVIGATIONS.COMMUNICATION) ||
    (!hasReviews && navSection === CANDIDATE_NAVIGATIONS.REVIEWS) ||
    (!hasComments && navSection === CANDIDATE_NAVIGATIONS.COMMENTS) ||
    (!hasTimelineResults && navSection === CANDIDATE_NAVIGATIONS.TIMELINE);
  const hasHiringActions = isItemInList(hiringTeam, auth);
  const isDisqualifiedUser = user.is_disqualified;
  const subdomain = getSubdomain(window.location.hostname);
  const reviewGroups = hasReviews
    ? Object.entries(reviews)
        .toReversed()
        .reduce((acc, review) => {
          const [, value] = review;
          return [...acc, { ...value }];
        }, [])
    : [];

  const getFileUrl = () => {
    if (user.cv && user.cv.includes('http')) return user.cv;
    if (subdomain && subdomain.length > 0) {
      return `${API_PROTOCOL}://${subdomain}.api.${API_DOMAIN}${user.cv}`;
    }
    return null;
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumberOfPages(numPages);
  };

  const getVirtualItemSize = useCallback(() => 109, []);

  const renderFieldList = fieldKey => {
    switch (fieldKey) {
      case CANDIDATE_PROFILE_INFO.EDUCATION: {
        return isArrayEmpty(user[fieldKey]) ? (
          <Typography
            variant="subtitle1"
            component="div"
            className={classes.sectionData}
          >
            -
          </Typography>
        ) : (
          <>
            {user[fieldKey].map((userInfo, index) => {
              const education = userInfo.degree
                ? `${userInfo.school}, ${userInfo.degree}`
                : userInfo.school;

              return (
                <div
                  key={`education_${index}`}
                  className={classes.timelineWrapper}
                >
                  <TextBoxWithTooltip
                    variant="body1"
                    className={classes.dateField}
                    component="div"
                    text={getPeriod(userInfo)}
                  />
                  <div className={classes.fieldWrapper}>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      className={classes.field}
                    >
                      {education}
                    </Typography>
                  </div>
                </div>
              );
            })}
          </>
        );
      }
      case CANDIDATE_PROFILE_INFO.EXPERIENCE: {
        return isArrayEmpty(user[fieldKey]) ? (
          <Typography
            variant="subtitle1"
            component="div"
            className={classes.sectionData}
          >
            -
          </Typography>
        ) : (
          <>
            {user[fieldKey].map((userInfo, index) => {
              return (
                <div
                  key={`experience_${index}`}
                  className={classes.timelineWrapper}
                >
                  <TextBoxWithTooltip
                    variant="body1"
                    className={classes.dateField}
                    component="div"
                    text={getPeriod(userInfo)}
                  />

                  <div className={classes.fieldWrapper}>
                    <Typography
                      variant="subtitle1"
                      component="div"
                      className={classes.field}
                    >
                      {`${userInfo.company}, ${userInfo.job_title}`}
                    </Typography>
                  </div>
                </div>
              );
            })}
          </>
        );
      }
      case CANDIDATE_PROFILE_INFO.SKILLS: {
        return isArrayEmpty(user[fieldKey]) ? (
          <Typography
            variant="subtitle1"
            component="div"
            className={classes.sectionData}
          >
            -
          </Typography>
        ) : (
          <div className={classes.fieldWrapper}>
            <Typography
              variant="body1"
              className={classes.field}
              component="div"
            >
              {user[fieldKey].map(skill => skill.name).join(', ')}
            </Typography>
          </div>
        );
      }
      case CANDIDATE_PROFILE_INFO.CV: {
        const fileUrl = getFileUrl();
        return user.cv && fileUrl ? (
          <>
            <div className={classes.scale}>
              <Link to={user.cv} target="_blank">
                <ZoomIcon />
              </Link>
            </div>
            <div className={classes.document}>
              <Document
                file={fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onContextMenu={e => e.preventDefault()}
              >
                {numberOfPages &&
                  Array.from({ length: numberOfPages }, (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      className={classes.pdfPage}
                    />
                  ))}
              </Document>
            </div>
          </>
        ) : (
          <Typography
            variant="body1"
            component="div"
            className={classes.sectionData}
          >
            -
          </Typography>
        );
      }
      case CANDIDATE_PROFILE_INFO.CONTACT_DETAILS: {
        return (
          <>
            {Object.values(CONTACT_DETAILS_INFO).map((value, index) => {
              switch (value) {
                case CONTACT_DETAILS_INFO.ADDRESS: {
                  return (
                    <div
                      key={`contact_info_${index}`}
                      className={classes.addressWrapper}
                    >
                      <TextBoxWithTooltip
                        variant="body1"
                        className={classes.field}
                        component="div"
                        text={`${
                          translations.candidateInfoFields[value]
                        }: ${getAddress(user)}`}
                      />
                    </div>
                  );
                }
                default: {
                  return (
                    <div
                      key={`contact_info_${index}`}
                      className={classes.addressWrapper}
                    >
                      <TextBoxWithTooltip
                        variant="body1"
                        className={classes.field}
                        component="div"
                        text={`${translations.candidateInfoFields[value]}: ${
                          user[value] ? user[value] : '-'
                        }`}
                      />
                    </div>
                  );
                }
              }
            })}
          </>
        );
      }
      default:
        return null;
    }
  };

  const renderProfile = () => {
    return (
      <div className={classes.profileWrapper}>
        <NotificationCard
          className={classes.uploadNotification}
          shouldFade={isOngoingCvParsing}
          content={translations.uploadCvMessage}
        />
        <Scrollbars verticalScrollClass={classes.scrollY}>
          <div className={classes.profileContent}>
            {Object.values(CANDIDATE_PROFILE_INFO).map(fieldKey => {
              const isCvField = CANDIDATE_PROFILE_INFO.CV !== fieldKey;
              return (
                <div key={fieldKey} className={classes.profileInfoSection}>
                  {isCvField && (
                    <Typography
                      variant="subtitle1"
                      className={classes.sectionField}
                    >
                      {translations.candidateInfoFields[fieldKey]}
                    </Typography>
                  )}
                  {isArray(user[fieldKey]) ||
                  [
                    CANDIDATE_PROFILE_INFO.CV,
                    CANDIDATE_PROFILE_INFO.CONTACT_DETAILS,
                  ].includes(fieldKey) ? (
                    renderFieldList(fieldKey)
                  ) : (
                    <Typography
                      variant="body1"
                      component="div"
                      className={classes.sectionData}
                    >
                      {user[fieldKey] || '-'}
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
        </Scrollbars>
      </div>
    );
  };

  const getTooltipText = (isAuthor, isRecordOnStep) => {
    if (isArchivedJob) return translations.archivedJob;
    if (isDisqualifiedUser) return translations.disqualifiedUserMessage;
    if (!isAuthor) return translations.madeByOtherUser;
    if (!isRecordOnStep) return translations.disabledButtonOtherStep;
    return '';
  };

  const renderComment = comment => {
    const isCommentAuthor = comment.author.id === auth.id;
    const isRecordOnStep = user.selection_step === comment.selection_step;
    const isDisabledDotsMenu = !(
      isCommentAuthor &&
      !isArchivedJob &&
      !isDisqualifiedUser &&
      isRecordOnStep
    );

    return (
      <CommunicationInfo
        translations={translations}
        record={comment}
        icon={CommentIcon}
        isDisabledDotsMenu={isDisabledDotsMenu}
        tooltipText={getTooltipText(isCommentAuthor, isRecordOnStep)}
        menuItems={getMenuItems(
          comment,
          translations,
          handleEditComment,
          handleDeleteComment
        )}
      />
    );
  };

  const renderEmailMessage = email => (
    <CommunicationInfo
      translations={translations}
      record={email}
      icon={EmailIcon}
      tooltipText={translations.disabledButtonCommunication}
      menuItems={getMenuItems(
        email,
        translations,
        handleEditComment,
        handleDeleteComment
      )}
      isDisabledDotsMenu
    />
  );

  const renderUsersInPendingState = users => (
    <div className={classes.pendingUsers}>
      <ListWithTooltip
        className={classes.listWithTooltip}
        items={users}
        asyncListProps={{
          position: 'top',
          isDisabled: true,
          shouldDisablePortal: false,
        }}
        visibleItemsCount={VISIBLE_USER_COUNT}
        isUserList
        isReadOnly
      />
      <Typography variant="body1">{translations.pendingUsersLabel}</Typography>
    </div>
  );

  const renderReviewGroup = reviewGroup => (
    <div
      className={classNames(classes.reviewsSectionWrap, {
        [classes.noPendingUsers]: isArrayEmpty(reviewGroup.pendingUsers),
      })}
    >
      <Typography variant="subtitle1">{reviewGroup.title}</Typography>

      {Object.values(reviewGroup.reviews)
        .toReversed()
        .map((userRecord, index) => {
          const isEvaluationReviewer = userRecord.reviewer.id === auth.id;
          const isRecordOnStep =
            user.selection_step === userRecord.selection_step.id;
          const isDisabledDotsMenu = !(
            isEvaluationReviewer &&
            !isArchivedJob &&
            !isDisqualifiedUser &&
            isRecordOnStep
          );
          return (
            <VoteInfo
              key={`review_${index}`}
              translations={translations}
              record={userRecord}
              isDisabledDotsMenu={isDisabledDotsMenu}
              tooltipText={getTooltipText(isEvaluationReviewer, isRecordOnStep)}
              menuItems={getMenuItems(
                userRecord,
                translations,
                handleVote,
                handleDeleteVote
              )}
            />
          );
        })}
      {!isArrayEmpty(reviewGroup.pendingUsers) &&
        renderUsersInPendingState(reviewGroup.pendingUsers)}
    </div>
  );

  const renderTimelineItem = timelineItem => {
    if (timelineItem.type === TIMELINE_MAP.JOB_EVENT) {
      return (
        <JobEvent translations={translations} record={timelineItem.data} />
      );
    }
    if (timelineItem.type === TIMELINE_MAP.COMMUNICATION) {
      return (
        <CommunicationInfo
          translations={translations}
          record={timelineItem.data}
          icon={EmailIcon}
          tooltipText={translations.disabledButtonCommunication}
          isDisabledDotsMenu
        />
      );
    }
    if (timelineItem.type === TIMELINE_MAP.COMMENT) {
      const isCommentAuthor = timelineItem.data.author.id === auth.id;
      const isRecordOnStep =
        user.selection_step === timelineItem.data.selection_step;
      const isDisabledDotsMenu = !(
        isCommentAuthor &&
        !isArchivedJob &&
        !isDisqualifiedUser &&
        isRecordOnStep
      );
      const tooltipText = getTooltipText(isCommentAuthor, isRecordOnStep);
      const menuItems = getMenuItems(
        timelineItem.data,
        translations,
        handleEditComment,
        handleDeleteComment
      );

      return (
        <CommunicationInfo
          translations={translations}
          record={timelineItem.data}
          icon={CommentIcon}
          isDisabledDotsMenu={isDisabledDotsMenu}
          tooltipText={tooltipText}
          menuItems={menuItems}
        />
      );
    }

    const isEvaluationReviewer = timelineItem.data.reviewer.id === auth.id;
    const isRecordOnStep =
      user.selection_step === timelineItem.data.selection_step.id;
    const isDisabledDotsMenu = !(
      isEvaluationReviewer &&
      !isArchivedJob &&
      !isDisqualifiedUser &&
      isRecordOnStep
    );
    const tooltipText = getTooltipText(isEvaluationReviewer, isRecordOnStep);
    const menuItems = getMenuItems(
      timelineItem.data,
      translations,
      handleVote,
      handleDeleteVote
    );

    return (
      <div
        className={classNames(
          classes.reviewsSectionWrap,
          classes.noPendingUsers
        )}
      >
        <Typography variant="subtitle1">
          {timelineItem.data.selection_step.title}
        </Typography>
        <VoteInfo
          translations={translations}
          record={timelineItem.data}
          isDisabledDotsMenu={isDisabledDotsMenu}
          tooltipText={tooltipText}
          menuItems={menuItems}
        />
      </div>
    );
  };

  const renderCandidateInfo = () => {
    switch (navSection) {
      case CANDIDATE_NAVIGATIONS.PROFILE: {
        return renderProfile();
      }
      case CANDIDATE_NAVIGATIONS.COMMUNICATION: {
        return hasEmails ? (
          <div className={classes.content}>
            <VirtualizedList
              verticalScrollClass={classes.scrollY}
              items={emails}
              count={emails?.length ?? 0}
              gap={32}
              estimateSize={getVirtualItemSize}
              renderItem={renderEmailMessage}
              isDynamicSize
            />
          </div>
        ) : (
          <NotificationCard
            className={classes.notificationCard}
            titleClass={classes.notificationTitleClass}
            customIcon={NoRecordsIcon}
            title={translations.noResults.communication.title}
            message={translations.noResults.communication.message}
            hasIcon
          />
        );
      }
      case CANDIDATE_NAVIGATIONS.COMMENTS: {
        return hasComments ? (
          <div className={classes.content}>
            <VirtualizedList
              verticalScrollClass={classes.scrollY}
              items={comments}
              count={comments?.length ?? 0}
              gap={32}
              estimateSize={getVirtualItemSize}
              renderItem={renderComment}
              isDynamicSize
            />
          </div>
        ) : (
          <NotificationCard
            className={classes.notificationCard}
            titleClass={classes.notificationTitleClass}
            customIcon={NoRecordsIcon}
            title={translations.noResults.comments.title}
            message={translations.noResults.comments.message}
            hasIcon
          />
        );
      }
      case CANDIDATE_NAVIGATIONS.REVIEWS: {
        return hasReviews ? (
          <div className={classes.content}>
            <VirtualizedList
              verticalScrollClass={classes.scrollY}
              items={reviewGroups}
              count={reviewGroups?.length ?? 0}
              gap={32}
              estimateSize={getVirtualItemSize}
              renderItem={renderReviewGroup}
              isDynamicSize
            />
          </div>
        ) : (
          <NotificationCard
            className={classes.notificationCard}
            titleClass={classes.notificationTitleClass}
            customIcon={NoRecordsIcon}
            title={translations.noResults.reviews.title}
            message={translations.noResults.reviews.message}
            hasIcon
          />
        );
      }
      default:
        return hasTimelineResults ? (
          <div className={classes.content}>
            <VirtualizedList
              verticalScrollClass={classes.scrollY}
              items={timelineResults}
              count={timelineResults?.length ?? 0}
              gap={32}
              estimateSize={getVirtualItemSize}
              hasNextPage={hasLoadMoreTimeline}
              isNextPageLoading={isTimelineLoading}
              renderItem={renderTimelineItem}
              onLoadMore={onLoadMore}
              isDynamicSize
            />
          </div>
        ) : (
          <NotificationCard
            className={classes.notificationCard}
            titleClass={classes.notificationTitleClass}
            customIcon={NoRecordsIcon}
            title={translations.noResults.timeline.title}
            message={translations.noResults.timeline.message}
            hasIcon
          />
        );
    }
  };

  const renderEditButton = () => (
    <div
      className={classNames({
        [classes.disabledButton]: isArchivedJob || isDisqualifiedUser,
      })}
    >
      <ActionButton
        type={ACTION_BUTTON_TYPES.EDIT}
        onClickHandler={handleEditDrawer}
        tooltipText={translations.edit}
        isSquared
      />
    </div>
  );

  return (
    <div className={classes.root}>
      <div className={classes.buttonWrapper}>
        <div className={classes.infoWrapper}>
          {(canEdit || hasHiringActions) && (
            <CustomButton
              className={classNames(classes.button, classes.addCommentButton, {
                [classes.disabledButton]: isArchivedJob || isDisqualifiedUser,
              })}
              type="commentDarkRoundedOutlined"
              onClick={handleAddComment}
            >
              {translations.buttons.addCommentLabel}
            </CustomButton>
          )}
          {canEdit && (
            <CustomButton
              className={classNames(classes.button, classes.addCommentButton, {
                [classes.disabledButton]:
                  isArchivedJob || isDisqualifiedUser || !isEmpty(user.email),
              })}
              type="emailDarkRoundedOutlined"
              onClick={handleEmailClick}
            >
              {translations.buttons.sendEmailLabel}
            </CustomButton>
          )}
          {(canEdit || hasHiringActions) && (
            <CustomButton
              className={classNames(classes.button, classes.addCommentButton, {
                [classes.disabledButton]: isArchivedJob || isDisqualifiedUser,
              })}
              type="darkRounded"
              onClick={() => handleVote(authVote)}
            >
              <VoteForIcon
                className={classNames(classes.voteFor, {
                  [classes.notSelected]:
                    hasAuthVote && authVote.vote === VOTE_MAP.REJECT,
                })}
              />
              {translations.voteLabel}
              <VoteAgainstIcon
                className={classNames(classes.voteAgainst, {
                  [classes.notSelected]:
                    hasAuthVote && authVote.vote === VOTE_MAP.APPROVE,
                })}
              />
            </CustomButton>
          )}
        </div>
        <div className={classes.infoWrapper}>
          {canEdit && (
            <ConditionalTooltip
              className={classNames(classes.tooltip)}
              message={
                isDisqualifiedUser
                  ? translations.buttons.qualify
                  : translations.buttons.disqualify
              }
              addTooltip
            >
              <ActionButton
                className={classNames(
                  classes.iconButton,
                  classes.disqualifyButton,
                  {
                    [classes.qualifyButton]: isDisqualifiedUser,
                    [classes.disabledButton]: isArchivedJob,
                  }
                )}
                type={
                  isDisqualifiedUser
                    ? ACTION_BUTTON_TYPES.QUALIFY
                    : ACTION_BUTTON_TYPES.DISQUALIFY
                }
                onClickHandler={() =>
                  isDisqualifiedUser ? handleQualify() : handleDisqualify()
                }
                isSquared
              />
            </ConditionalTooltip>
          )}
          {canEdit && (
            <ConditionalTooltip
              className={classNames(classes.tooltip)}
              message={translations.disqualifiedUserMessage}
              addTooltip={isDisqualifiedUser}
            >
              <DropdownMenu
                menuItems={getMoveCandidateToStepActionItems(
                  steps,
                  candidateStep,
                  handleMoveToStep
                )}
                isDisabled={isDisqualifiedUser || isArchivedJob}
                buttonLabel={translations.buttons.moveToStepLabel}
                isDarkButton
              />
            </ConditionalTooltip>
          )}
        </div>
      </div>
      <Candidate
        className={classes.candidate}
        user={user}
        label={user.profession_title}
        hasEdit={canEdit}
        editButton={renderEditButton}
        hasLabel
      />
      {hasContactInfo && (
        <div className={classes.info}>
          {(user[ADDRESS_INFO.ADDRESS] ||
            user[ADDRESS_INFO.CITY] ||
            user[ADDRESS_INFO.COUNTRY]) && (
            <div className={classes.infoItem}>
              <LocationIcon className={classes.icon} />
              <TextBoxWithTooltip variant="body1" text={getAddress(user)} />
            </div>
          )}
          {user.email && (
            <div className={classes.infoItem}>
              <EmailIcon className={classes.icon} />
              <TextBoxWithTooltip variant="body1" text={user.email} />
            </div>
          )}
          {user.phone && (
            <div className={classes.infoItem}>
              <PhoneIcon className={classes.icon} />
              <TextBoxWithTooltip variant="body1" text={user.phone || '-'} />
            </div>
          )}
          {user.referral && (
            <div className={classes.infoItem}>
              <PersonIcon className={classes.icon} />
              <TextBoxWithTooltip
                variant="body1"
                text={
                  user.referral
                    ? `${user.referral} (${translations.referral})`
                    : '-'
                }
              />
            </div>
          )}
        </div>
      )}
      <HorizontalNavigation
        navItems={candidateNavigationItems}
        className={classNames(classes.navWrapper, {
          [classes.noResultsNavWrapper]: hasNoResults,
        })}
        customNavItemClass={classes.customNavItem}
      />
      {!isLoadingData && renderCandidateInfo()}
    </div>
  );
};

CandidateInfo.propTypes = {
  translations: PropTypes.shape({}).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
  isArchivedJob: PropTypes.bool.isRequired,
  isTimelineLoading: PropTypes.bool.isRequired,
  hasLoadMoreTimeline: PropTypes.bool.isRequired,
  auth: PropTypes.object.isRequired,
  canEdit: PropTypes.bool.isRequired,
  user: PropTypes.shape({}).isRequired,
  navSection: PropTypes.string.isRequired,
  candidateNavigationItems: PropTypes.array.isRequired,
  handleDeleteComment: PropTypes.func.isRequired,
  handleDeleteVote: PropTypes.func.isRequired,
  candidateStep: PropTypes.number.isRequired,
  handleEditComment: PropTypes.func.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  isOngoingCvParsing: PropTypes.bool.isRequired,
  steps: PropTypes.arrayOf(PropTypes.shape({})),
  reviews: PropTypes.shape({}),
  hiringTeam: PropTypes.arrayOf(PropTypes.shape({})),
  comments: PropTypes.arrayOf(PropTypes.shape({})),
  emails: PropTypes.arrayOf(PropTypes.shape({})),
  timeline: PropTypes.shape({}),
  handleEditDrawer: PropTypes.func,
  handleMoveToStep: PropTypes.func,
  handleDisqualify: PropTypes.func,
  handleQualify: PropTypes.func,
  handleVote: PropTypes.func,
  handleAddComment: PropTypes.func,
  handleEmailClick: PropTypes.func,
  authVote: PropTypes.object,
};

export default CandidateInfo;
